import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import ExportButton from './ExportButton';
import EventsFilters from './EventsFilters';
import { useEventsBar, setEventsBar } from '../../hooks/useEventsBar';
import { useStoredEvents } from '../../hooks/useStoredEvents';
import { useRole } from '../../hooks/useRole';

export const EVENT_RESTORE = 'event-restore';
export const EVENT_PERMANENT_DELETE = 'event-permanent-delete';

const distinct = (values: Array<string | undefined>) =>
  [...new Set(values.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b));

/** Breadcrumb actions of the Events list: Archived switch, Filters popover, New, Export. */
export const EventsListBar: React.FC = () => {
  const navigate = useNavigate();
  const bar = useEventsBar();
  const events = useStoredEvents();
  const { canEdit } = useRole();
  const categories = useMemo(() => distinct(events.map(e => e.category)), [events]);
  const statuses = useMemo(() => distinct(events.map(e => e.status)), [events]);

  return (
    <>
      <div className="header-filter-group">
        <ArchivedToggle checked={bar.archived} onChange={archived => setEventsBar({ archived })} />
      </div>
      {!bar.archived && (
        <EventsFilters
          category={bar.category}
          onCategoryChange={category => setEventsBar({ category })}
          status={bar.status}
          onStatusChange={status => setEventsBar({ status })}
          categories={categories}
          statuses={statuses}
        />
      )}
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate('/events/new')} title="New Event">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Event</span>
        </button>
      )}
      <ExportButton exportKey="events" />
    </>
  );
};

/** Breadcrumb actions of one event: Save and Archive while it is live; Restore and Delete once archived. */
export const EventDetailBar: React.FC<{ onSave?: () => void; onArchive?: () => void; onCancel: () => void; isNew?: boolean }> = ({
  onSave, onArchive, onCancel, isNew,
}) => {
  const { detail } = useEventsBar();
  const { canEdit } = useRole();
  if (!canEdit) {
    return (
      <button type="button" className="header-action-btn" onClick={onCancel}>Back</button>
    );
  }
  if (isNew) {
    return (
      <>
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={onSave} title="Save">
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Save</span>
        </button>
        <button type="button" className="header-action-btn" onClick={onCancel}>Cancel</button>
      </>
    );
  }
  if (detail?.archived) {
    return (
      <>
        <button type="button" className="header-action-btn" onClick={() => window.dispatchEvent(new CustomEvent(EVENT_RESTORE))} title="Restore this event">
          Restore
        </button>
        <button
          type="button"
          className="header-action-btn header-action-btn--iconic header-btn-danger"
          onClick={() => window.dispatchEvent(new CustomEvent(EVENT_PERMANENT_DELETE))}
          title="Permanently delete this event"
        >
          <Icon name="trash" size={16} />
          <span className="header-action-btn__label">Delete</span>
        </button>
      </>
    );
  }
  return (
    <>
      <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={onSave} title="Save Changes">
        <Icon name="check" size={16} />
        <span className="header-action-btn__label">Save Changes</span>
      </button>
      <div className="header-divider" />
      <button type="button" className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={onArchive} title="Archive this event">
        <Icon name="trash" size={16} />
        <span className="header-action-btn__label">Archive</span>
      </button>
    </>
  );
};
